import React, { useState } from 'react';
import { MainLayout } from '../../../../components/MainLayout';
import { InvoicesTable } from './components/InvoicesTable';
import styles from './InvoiceList.styles.scss';
import { ROUTES } from './InvoiceList.config';
import cn from 'classnames';
import { PagesHeader } from '../../../../components/shared/PagesHeader';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { useNavigate } from 'react-router';
import {
  authorizeWebApplication,
  deleteDraftInvoice,
  getUserAuthorization,
} from '../../../../services/apis';
import { useAuthentication } from '../../../../hooks/useAuthentication';
import DeleteInvoiceDialog from './components/InvoicesTable/components/DeleteInvoiceDialog/DeleteInvoiceDialog';
import { PERMISSIONS } from '../../../../helpers/auth';
import { InvoiceStatistics } from '../../../../components/shared/InvoiceStatistics';

const InvoiceList = ({
  statistics,
  invoices,
  setRenderInvoices,
  loading,
  reloadInvoices,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userInfo, hasRole } = useAuthentication();
  const [authorizationDialogVisible, setAuthorizationDialogVisible] =
    useState(false);
  const [limitExceededDialogVisible, setLimitExceededDialogVisible] =
    useState(false);

  const [apiLoading, setAPILoading] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [deleteApiLoading, setDeleteApiLoading] = useState(false);
  const [authorizeApiLoading, setAuthorizeApiLoading] = useState(false);

  const progressPercentage = (
    ((statistics.thisYear ?? 0) / statistics.limit) *
    100
  ).toFixed(1);

  const checkAuthorization = () => {
    setAPILoading(true);
    getUserAuthorization(userInfo.tax_number)
      .then(() => {
        navigate('./create');
      })
      .catch(() => {
        setAuthorizationDialogVisible(true);
      })
      .finally(() => {
        setAPILoading(false);
      });
  };

  const confirmDelete = (rowData) => {
    setDeleteApiLoading(true);
    return deleteDraftInvoice(
      rowData.invoiceUniqueIdentifier,
      userInfo.tax_number,
    )
      .then(() => {
        reloadInvoices();
      })
      .finally(() => {
        setDeleteApiLoading(false);
      });
  };

  return (
    <MainLayout eInvoicing={true} className={cn(styles.container)}>
      <PagesHeader
        withBreadcrumb={true}
        routes={ROUTES}
        titleClassName={styles.title}
        wrapperClassName={'mw-100'}
        withActionButton
        action={
          hasRole(PERMISSIONS.GENERATE_INVOICE) ? (
            <Button
              loading={apiLoading || loading}
              disabled={
                apiLoading || loading || !hasRole(PERMISSIONS.GENERATE_INVOICE)
              }
              label={t('generate_einvoice')}
              onClick={() => {
                if (progressPercentage == 100) {
                  setLimitExceededDialogVisible(true);
                } else {
                  checkAuthorization();
                }
              }}
              tooltipOptions={{ position: 'top' }}
              tooltip={t('generate_einvoice_tooltip')}
              icon="pi pi-info-circle"
              iconPos="right"
            />
          ) : null
        }
      >
        <InvoiceStatistics statistics={statistics} title={t('sme')} />
      </PagesHeader>
      <div>
        <InvoicesTable
          invoices={invoices}
          loading={loading}
          setDeleteRow={setDeleteRow}
        />
      </div>
      <Dialog
        className="rounded-4 overflow-hidden w-50"
        visible={authorizationDialogVisible}
        onHide={() => {
          setAuthorizationDialogVisible(false);
        }}
        focusOnShow={false}
        closeIcon={'pi pi-times color-black'}
        contentClassName="pt-3"
        header={t('terms-and-conditions')}
        footer={
          <Button
            label={t('accept-the-terms-and-conditions')}
            className={'w-100 mt-4'}
            loading={authorizeApiLoading}
            disabled={authorizeApiLoading}
            onClick={() => {
              setAuthorizeApiLoading(true);
              authorizeWebApplication(userInfo.tax_number)
                .then(() => {
                  navigate('./create');
                })
                .finally(() => {
                  setAuthorizeApiLoading(false);
                });
            }}
          />
        }
      >
        <div
          className="termsAndConditions"
          dangerouslySetInnerHTML={{
            __html: t('invoice_list.authorization_terms_desc'),
          }}
        />
      </Dialog>
      <DeleteInvoiceDialog
        loading={deleteApiLoading}
        deleteRow={deleteRow}
        setDeleteRow={setDeleteRow}
        confirmDelete={confirmDelete}
      />
      <Dialog
        className="rounded-4 overflow-hidden w-50"
        visible={limitExceededDialogVisible}
        onHide={() => {
          setLimitExceededDialogVisible(false);
        }}
        header={t('e-invoice-limit-exceeded')}
        footer={
          <Button
            label={t('common.back')}
            className={'w-100'}
            onClick={() => {
              setLimitExceededDialogVisible(false);
            }}
          />
        }
      >
        <div className={styles.warning}>
          <span
            className={cn('pi icn-info-circle-filled', styles.warningIcon)}
          />
          {t('invoice_list.limit_exceeded_desc')}
        </div>
      </Dialog>
    </MainLayout>
  );
};

export default React.memo(InvoiceList);
